import { isRouteErrorResponse, useLocation, useRouteError } from 'react-router';
import { Provider } from 'react-redux';
import ErrorPage from './shared/components/ErrorPage';
import '@pmi/dsm-react/dist/assets/styles/styles.scss';
import AuthService from './shared/services/AuthService';
import { getMyPMISiteUrl, getPMISiteUrl } from './shared/env-vars';
import { store } from './shared/store';
import AppHelmet from './shared/components/AppHelmet';
import Layout from './shared/navigation/Layout/index';

export default function RootBoundary({
  error = {
    status: 404,
  },
}: {
  error?: any;
}) {
  const location = useLocation();

  if (error.status === 404) {
    return (
      <ErrorPage status={error.status} message="Page Not Found">
        <h2>The page you are looking for cannot be found.</h2>
        <p>You may have followed a bad link or incorrectly typed the URL.</p>
        <p>
          Follow these popular links to find what you&apos;re looking for:{' '}
          <br /> <a href={getPMISiteUrl()}>Home</a> |{' '}
          <a href="/hub">Partner Platform</a> |{' '}
          <a href={getMyPMISiteUrl()} target="_blank" rel="noopener noreferrer">
            myPMI
          </a>
        </p>
      </ErrorPage>
    );
  }

  if (error.status === 401 || error?.message === 'Popup closed by user') {
    // A popup login already in flight will reload the page once it resolves;
    // starting a competing redirect here corrupts its OIDC state. Otherwise,
    // AuthService.login() has its own guard against repeated redirects, so
    // this can never loop forever even if the redirect flow keeps failing.
    if (window.localStorage.getItem('signinpopup') !== 'open') {
      const authService = new AuthService();
      authService.login(location.pathname + location.search);
    }
    return <ErrorPage status={error.status} message="Unauthorized" />;
  }

  if (error.status === 403) {
    return <ErrorPage status={error.status} message="Unauthorized" />;
  }

  if (error.status === 503 || error.status === 500) {
    return <ErrorPage status={error.status} message="Internal Server Error" />;
  }

  return <ErrorPage status={error.status} message="Unexpected Error" />;
}

export function RootRouteErrorBoundary() {
  const error = useRouteError();

  return (
    <Provider store={store}>
      <AppHelmet />
      <Layout>
        {isRouteErrorResponse(error) && <RootBoundary error={error} />}
      </Layout>
    </Provider>
  );
}
